/**
 * 国内常见坐标系转换
 * - BD-09：百度地图
 * - GCJ-02：高德 / 腾讯 / 国内业务系统（火星坐标）
 * - WGS84：GPS / Cesium Ion / 国际底图
 *
 * 本项目约定：接口样例坐标按 GCJ-02 处理，上图前统一转 WGS84。
 */

const PI = Math.PI;
const X_PI = (PI * 3000.0) / 180.0;
const A = 6378245.0;
const EE = 0.00669342162296594323;

function outOfChina(lng, lat) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function transformLat(lng, lat) {
  let ret =
    -100.0 +
    2.0 * lng +
    3.0 * lat +
    0.2 * lat * lat +
    0.1 * lng * lat +
    0.2 * Math.sqrt(Math.abs(lng));
  ret +=
    ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) *
      2.0) /
    3.0;
  ret +=
    ((20.0 * Math.sin(lat * PI) + 40.0 * Math.sin((lat / 3.0) * PI)) * 2.0) /
    3.0;
  ret +=
    ((160.0 * Math.sin((lat / 12.0) * PI) +
      320 * Math.sin((lat * PI) / 30.0)) *
      2.0) /
    3.0;
  return ret;
}

function transformLng(lng, lat) {
  let ret =
    300.0 +
    lng +
    2.0 * lat +
    0.1 * lng * lng +
    0.1 * lng * lat +
    0.1 * Math.sqrt(Math.abs(lng));
  ret +=
    ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) *
      2.0) /
    3.0;
  ret +=
    ((20.0 * Math.sin(lng * PI) + 40.0 * Math.sin((lng / 3.0) * PI)) * 2.0) /
    3.0;
  ret +=
    ((150.0 * Math.sin((lng / 12.0) * PI) +
      300.0 * Math.sin((lng / 30.0) * PI)) *
      2.0) /
    3.0;
  return ret;
}

function delta(lng, lat) {
  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);
  const radLat = (lat / 180.0) * PI;
  let magic = Math.sin(radLat);
  magic = 1 - EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / (((A * (1 - EE)) / (magic * sqrtMagic)) * PI);
  dLng = (dLng * 180.0) / ((A / sqrtMagic) * Math.cos(radLat) * PI);
  return { dLng, dLat };
}

/** WGS84 → GCJ-02 */
export function wgs84ToGcj02(lng, lat) {
  if (outOfChina(lng, lat)) {
    return { lng, lat };
  }
  const { dLng, dLat } = delta(lng, lat);
  return {
    lng: lng + dLng,
    lat: lat + dLat,
  };
}

/**
 * GCJ-02 → WGS84（迭代精算，比一次性近似更准）
 */
export function gcj02ToWgs84(lng, lat) {
  if (outOfChina(lng, lat)) {
    return { lng, lat };
  }
  let wgsLng = lng;
  let wgsLat = lat;
  for (let i = 0; i < 8; i++) {
    const gcj = wgs84ToGcj02(wgsLng, wgsLat);
    wgsLng += lng - gcj.lng;
    wgsLat += lat - gcj.lat;
  }
  return { lng: wgsLng, lat: wgsLat };
}

/** BD-09 → GCJ-02 */
export function bd09ToGcj02(lng, lat) {
  const x = lng - 0.0065;
  const y = lat - 0.006;
  const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * X_PI);
  const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * X_PI);
  return {
    lng: z * Math.cos(theta),
    lat: z * Math.sin(theta),
  };
}

/** BD-09 → WGS84 */
export function bd09ToWgs84(lng, lat) {
  const gcj = bd09ToGcj02(lng, lat);
  return gcj02ToWgs84(gcj.lng, gcj.lat);
}

/**
 * 业务坐标 → Cesium 度坐标（WGS84）
 * @param {'gcj02'|'wgs84'|'bd09'} [from='gcj02']
 */
export function toCesiumDegrees(lng, lat, from = "gcj02") {
  const x = Number(lng);
  const y = Number(lat);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  if (from === "wgs84") return { lng: x, lat: y };
  if (from === "bd09") return bd09ToWgs84(x, y);
  return gcj02ToWgs84(x, y);
}
